import { useCallback } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { Input, Label, TextArea } from '@shared/components';
import { REASON_MAX_LENGTH } from '../../schemas/accessJustification.schema';
import type { AccessPromptFormInput, AccessPromptFormValues } from '../../schemas/accessPrompt.schema';

interface Props {
    idPrefix: string;
    control: Control<AccessPromptFormInput, unknown, AccessPromptFormValues>;
    errors: FieldErrors<AccessPromptFormInput>;
    disabled: boolean;
}

export function AccessJustificationFields({ idPrefix, control, errors, disabled }: Props) {
    const ids = { caseReference: `${idPrefix}-case-reference`, reason: `${idPrefix}-reason` };

    const reasonErrorId = errors.reason ? `${ids.reason}-error` : undefined;
    // The shared TextArea exposes no ARIA props, so the attributes are set on its textarea here.
    const reasonWrapperRef = useCallback(
        (wrapper: HTMLDivElement | null) => {
            const textarea = wrapper?.querySelector('textarea');
            if (!textarea) return;
            if (reasonErrorId) {
                textarea.setAttribute('aria-invalid', 'true');
                textarea.setAttribute('aria-describedby', reasonErrorId);
            } else {
                textarea.removeAttribute('aria-invalid');
                textarea.removeAttribute('aria-describedby');
            }
        },
        [reasonErrorId],
    );

    return (
        <>
            <div>
                <Label htmlFor={ids.caseReference}>Case number</Label>
                <Controller
                    name="caseReference"
                    control={control}
                    render={({ field }) => (
                        <Input
                            id={ids.caseReference}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            autoComplete="off"
                            error={!!errors.caseReference}
                            aria-invalid={!!errors.caseReference}
                            aria-describedby={errors.caseReference ? `${ids.caseReference}-error` : undefined}
                            disabled={disabled}
                        />
                    )}
                />
                <FieldError id={`${ids.caseReference}-error`} message={errors.caseReference?.message} />
            </div>

            <div>
                <Label htmlFor={ids.reason}>Reason for access</Label>
                <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                        <div ref={reasonWrapperRef} onBlur={field.onBlur}>
                            <TextArea
                                id={ids.reason}
                                placeholder="Why do you need to open this Agreement?"
                                rows={4}
                                value={field.value}
                                onChange={field.onChange}
                                error={!!errors.reason}
                                disabled={disabled}
                            />
                            <p className="mt-1 text-right text-xs text-mp-muted">
                                {field.value.length} / {REASON_MAX_LENGTH}
                            </p>
                        </div>
                    )}
                />
                <FieldError id={`${ids.reason}-error`} message={errors.reason?.message} />
            </div>
        </>
    );
}

function FieldError({ id, message }: { id: string; message?: string }) {
    if (!message) return null;

    return (
        <p id={id} role="alert" className="mt-1.5 text-xs text-mp-error">
            {message}
        </p>
    );
}
